"use client"

import * as React from "react"
import { AppSidebar } from "./app-sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "./breadcrumb"
import { Separator } from "./separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "./sidebar"

// Import the Patient Table component
import { 
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import { Button } from './button'
import { Input } from './input'
import { Badge } from './badge'
import { Checkbox } from './checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { MedicationCard } from './medication-card'
import { authService } from '@joinomu/shared'

// Import Supabase client
import { supabase } from '@joinomu/shared'

// Patient data type with relationship info
export type Patient = {
  id: string
  profile_id: string
  name: string
  careTeam?: string[] // Optional for compatibility with admin dashboard
  treatments?: string[] // Optional for compatibility with admin dashboard
  medications: string[]
  status: string[]
  treatmentType?: string
  assignedDate?: string
  isPrimary?: boolean
  email?: string
  hasCompletedIntake?: boolean
  // Additional properties for compatibility with patient-table
  first_name?: string
  last_name?: string
  created_at?: string
  phone?: string
  date_of_birth?: string
  has_completed_intake?: boolean
  assigned_providers?: string[]
  treatment_types?: string[]
  // Appointment data
  previous_appointment?: {
    date: string
    type: string
  }
  next_appointment?: {
    date: string
    type: string
  }
}


export type ProviderVisit = {
  id: string
  patient_id: string
  patient_name: string
  provider_name: string
  appointment_date: string
  start_time: string
  treatment_type: string
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled'
  is_async?: boolean
}

// Sample patient data (this will be replaced with real Supabase data)
const patientData: Patient[] = [
  {
    id: "1",
    profile_id: "profile_1",
    name: "Sarah Johnson",
    medications: ["Semaglutide", "Metformin"],
    status: ["Active", "In Progress"],
    assignedDate: "2024-01-15",
    isPrimary: true,
    previous_appointment: {
      date: "2024-11-01",
      type: "Follow-up"
    },
    next_appointment: {
      date: "2024-12-15",
      type: "Consultation"
    }
  },
  {
    id: "2", 
    profile_id: "profile_2",
    name: "Michael Roberts",
    medications: ["Lisinopril", "Atorvastatin"],
    status: ["Stable", "Monthly Review"],
    assignedDate: "2024-02-01",
    isPrimary: false,
    previous_appointment: {
      date: "2024-10-15",
      type: "Check-up"
    },
    next_appointment: {
      date: "2024-11-20",
      type: "Follow-up"
    }
  },
  {
    id: "3",
    profile_id: "profile_3",
    name: "Jennifer Martinez",
    medications: ["Sertraline", "Alprazolam"],
    status: ["Improving", "Weekly Check"],
    assignedDate: "2024-01-30",
    isPrimary: true,
    previous_appointment: {
      date: "2024-11-05",
      type: "Therapy Session"
    }
  },
  {
    id: "4",
    profile_id: "profile_4",
    name: "David Anderson",
    medications: ["Ozempic", "Victoza"],
    status: ["New Patient", "Education Needed"],
    assignedDate: "2024-03-01",
    isPrimary: false,
    next_appointment: {
      date: "2024-11-25",
      type: "Initial Consultation"
    }
  },
]

function TagList({ items, variant = "secondary" }: { items: string[], variant?: string }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, index) => (
        <Badge key={index} variant={variant as any} className="text-xs">
          {item}
        </Badge>
      ))}
    </div>
  )
}


function ProviderVisits({
  providerId,
  assignedPatients,
  onVisitClick,
  onCountChange,
  onLoadingChange
}: {
  providerId?: string
  assignedPatients?: Patient[]
  onVisitClick?: (visit: ProviderVisit) => void
  onCountChange?: (count: number) => void
  onLoadingChange?: (loaded: boolean) => void
}) {
  const [rawVisits, setRawVisits] = React.useState<ProviderVisit[]>([])
  const [loading, setLoading] = React.useState(true)
  const [providerTimezone, setProviderTimezone] = React.useState<string>('America/New_York')

  // Fetch provider's timezone from their profile
  React.useEffect(() => {
    const fetchProviderTimezone = async () => {
      if (!providerId) return
      try {
        const { data } = await supabase
          .from('profiles')
          .select('timezone')
          .eq('id', providerId)
          .single()
        if (data?.timezone) {
          setProviderTimezone(data.timezone)
          console.log('🌍 Provider timezone:', data.timezone)
        }
      } catch (err) {
        console.warn('Could not fetch provider timezone, using default:', err)
      }
    }
    fetchProviderTimezone()
  }, [providerId])

  // Use raw visits directly - filtering by Prescription filled is handled server-side
  const visits = rawVisits

  React.useEffect(() => {
    if (providerId) {
      fetchProviderVisits()
    }
  }, [providerId])

  React.useEffect(() => {
    onCountChange?.(visits.length)
  }, [visits.length, onCountChange])

  React.useEffect(() => {
    onLoadingChange?.(!loading)
  }, [loading, onLoadingChange])

  const fetchProviderVisits = async () => {
    try {
      if (!providerId) {
        console.log('🔍 No provider ID provided for visits fetch')
        setRawVisits([])
        setLoading(false)
        return
      }

      console.log('🔍 Fetching provider appointments directly by provider ID:', providerId)

      // Use the new getProviderAppointments method to fetch appointments directly by provider
      const result = await authService.getProviderAppointments(providerId)

      if (result.data && result.data.length > 0) {
        console.log('🔍 Raw provider appointments:', result.data)

        // Transform appointments to ProviderVisit format
        const providerVisits = result.data.map(appt => ({
          id: appt.id,
          patient_id: appt.patient_id,
          patient_name: appt.patient_name || `Patient ${appt.patient_id?.slice(0,8)}`,
          provider_name: 'Dr. Provider', // TODO: Get actual provider name from appointment
          appointment_date: appt.appointment_date,
          start_time: appt.start_time,
          treatment_type: appt.treatment_type || 'consultation',
          status: appt.status as 'scheduled' | 'confirmed' | 'completed' | 'cancelled',
          is_async: appt.is_async === true
        }))

        console.log('✅ Transformed provider visits:', providerVisits)
        setRawVisits(providerVisits)
      } else {
        console.log('🔍 No appointments found for provider')
        setRawVisits([])
      }
    } catch (error) {
      console.error('Error fetching provider visits:', error)
      setRawVisits([])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p>Loading visits...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {visits.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No scheduled visits found.
        </div>
      ) : (
        <div className="space-y-4">
          {visits.map((visit) => (
            <MedicationCard
              key={visit.id}
              medicationName={visit.patient_name}
              dosage={visit.treatment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              supply={`${(() => {
                const [year, month, day] = visit.appointment_date.split('-').map(Number)
                const date = new Date(year, month - 1, day)
                return date.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })
              })()} at ${(() => {
                // Parse and format the time to PST AM/PM format
                const timeString = visit.start_time
                
                // If time is already in AM/PM format, return as is
                if (timeString && /\d{1,2}:\d{2}\s*(AM|PM)/i.test(timeString)) {
                  return timeString
                }
                
                // If time is in 24-hour format like "14:30:00" or "14:30"
                if (timeString && /\d{1,2}:\d{2}(:\d{2})?$/.test(timeString)) {
                  const [hours, minutes] = timeString.split(':').map(Number)
                  const date = new Date()
                  date.setHours(hours, minutes, 0, 0)
                  
                  // Format in provider's timezone
                  return date.toLocaleTimeString('en-US', {
                    timeZone: providerTimezone,
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })
                }
                
                // Fallback to original time string
                return timeString || 'Time TBD'
              })()}`}
              status={visit.status as "active" | "pending" | "approved" | "denied" | "discontinued" | "shipped" | "delivered" | "scheduled"}
              isAsync={visit.is_async}
              onTitleClick={() => onVisitClick?.(visit)}
              className="w-full max-w-none cursor-pointer"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function PatientTable({ 
  patients: initialPatients, 
  providerId,
  onPatientClick
}: { 
  patients?: Patient[]
  providerId?: string
  onPatientClick?: (patient: Patient) => void
}) {
  const [globalFilter, setGlobalFilter] = React.useState("")
  const [selectedRows, setSelectedRows] = React.useState<string[]>([])
  const [patients, setPatients] = React.useState<Patient[]>(initialPatients || [])
  const [loading, setLoading] = React.useState(!initialPatients)

  // Use provided patients or fetch if none provided
  React.useEffect(() => {
    if (initialPatients && initialPatients.length > 0) {
      setPatients(initialPatients)
      setLoading(false)
    } else if (providerId) {
      fetchProviderPatients()
    }
  }, [providerId, initialPatients])

  const fetchProviderPatients = async () => {
    try {
      console.log('🔍 PROVIDER DASHBOARD: fetchProviderPatients called - this should not be used anymore!')
      console.log('🔍 PROVIDER DASHBOARD: providerId:', providerId)
      console.log('🔍 PROVIDER DASHBOARD: initialPatients:', initialPatients?.length)
      
      // This fallback query is now disabled - auth service should handle all patient fetching
      console.log('⚠️ PROVIDER DASHBOARD: Skipping fallback query - relying on auth service data only')
      setPatients([])
    } catch (error) {
      console.error('Error in fetchProviderPatients fallback:', error)
      setPatients([])
    } finally {
      setLoading(false)
    }
  }

  // Filter patients (treatment type filter removed)
  const filteredPatients = React.useMemo(() => {
    return patients
  }, [patients])

  // Handle individual row selection
  const handleRowSelect = (patientId: string, checked: boolean) => {
    if (checked) {
      setSelectedRows(prev => [...prev, patientId])
    } else {
      setSelectedRows(prev => prev.filter(id => id !== patientId))
    }
  }

  // Define table columns
  const columns = React.useMemo<ColumnDef<Patient>[]>(() => [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={selectedRows.length === table.getFilteredRowModel().rows.length && table.getFilteredRowModel().rows.length > 0}
          onCheckedChange={(checked) => {
            if (checked) {
              setSelectedRows(table.getFilteredRowModel().rows.map(row => row.original.id))
            } else {
              setSelectedRows([])
            }
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedRows.includes(row.original.id)}
          onCheckedChange={(checked) => handleRowSelect(row.original.id, !!checked)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <button 
          className="font-medium text-left hover:text-primary hover:underline cursor-pointer"
          onClick={() => onPatientClick?.(row.original)}
        >
          {row.getValue("name")}
        </button>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const patient = row.original
        const hasCompletedIntake = patient.has_completed_intake || patient.hasCompletedIntake
        const isPrimary = patient.isPrimary
        
        return (
          <div className="flex items-center gap-1">
            <Badge 
              variant={hasCompletedIntake ? "default" : "secondary"} 
              className="text-xs"
            >
              {hasCompletedIntake ? "Active" : "Pending"}
            </Badge>
            {isPrimary && (
              <Badge variant="outline" className="text-xs">
                Primary
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: "previousVisit",
      header: "Previous Visit",
      cell: ({ row }) => {
        const patient = row.original
        if (patient.previous_appointment) {
          // Parse date manually to avoid timezone issues
          const [year, month, day] = patient.previous_appointment.date.split('-').map(Number)
          const appointmentDate = new Date(year, month - 1, day) // month is 0-indexed
          const formattedDate = appointmentDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          })
          return (
            <div className="text-sm">
              <div className="font-medium">{formattedDate}</div>
              <div className="text-muted-foreground text-xs">{patient.previous_appointment.type}</div>
            </div>
          )
        }
        return (
          <div className="text-sm text-muted-foreground">
            No previous visits
          </div>
        )
      },
    },
    {
      accessorKey: "nextVisit", 
      header: "Next Visit",
      cell: ({ row }) => {
        const patient = row.original
        if (patient.next_appointment) {
          // Parse date manually to avoid timezone issues
          const [year, month, day] = patient.next_appointment.date.split('-').map(Number)
          const appointmentDate = new Date(year, month - 1, day) // month is 0-indexed
          const formattedDate = appointmentDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          })
          return (
            <div className="text-sm">
              <div className="font-medium">{formattedDate}</div>
              <div className="text-muted-foreground text-xs">{patient.next_appointment.type}</div>
            </div>
          )
        }
        return (
          <div className="text-sm text-muted-foreground">
            No upcoming visits
          </div>
        )
      },
    },
  ], [selectedRows])

  const table = useReactTable({
    data: filteredPatients,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    initialState: {
      pagination: {
        pageSize: 8,
      },
    },
  })

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex items-center justify-center h-64">
          <p>Loading patients...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full p-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-4">
          <Input
            placeholder="Search patients..."
            value={globalFilter ?? ""}
            onChange={(event) => setGlobalFilter(String(event.target.value))}
            className="max-w-sm"
          />
          
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="font-medium">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`${selectedRows.includes(row.original.id) ? "bg-muted" : ""}`}
                  style={{ height: '21px' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-1">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="text-sm text-muted-foreground">
          {selectedRows.length} of {table.getFilteredRowModel().rows.length} row(s) selected
        </div>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value))
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[4, 8, 10, 20].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ProviderDashboard({
  user,
  onLogout,
  activeItem = "Dashboard",
  showPatientTable = false,
  onNavigate,
  providerId,
  assignedPatients,
  onPatientClick,
  onVisitClick,
  onAccountClick,
  onSupportFeedbackClick
}: {
  user?: {
    name: string
    email: string
    avatar?: string
  }
  onLogout?: () => void
  activeItem?: string
  showPatientTable?: boolean
  onNavigate?: (item: string) => void
  providerId?: string
  assignedPatients?: Patient[]
  onPatientClick?: (patient: Patient) => void
  onVisitClick?: (visit: ProviderVisit) => void
  onAccountClick?: () => void
  onSupportFeedbackClick?: (type: "Support" | "Feedback") => void
}) {
  const [visitsCount, setVisitsCount] = React.useState(0)
  const [visitsLoaded, setVisitsLoaded] = React.useState(false)

  // Memoize callback functions to prevent infinite loops
  const handleVisitsCountChange = React.useCallback((count: number) => {
    setVisitsCount(count)
  }, [])

  const handleVisitsLoadingChange = React.useCallback((loaded: boolean) => {
    setVisitsLoaded(loaded)
  }, [])

  return (
    <SidebarProvider>
        <AppSidebar 
          user={user} 
          onLogout={onLogout} 
          onNavigate={onNavigate} 
          userRole="provider" 
          onAccountClick={onAccountClick}
          onSupportFeedbackClick={onSupportFeedbackClick}
        />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2">
            <div className="flex items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator
                orientation="vertical"
                className="mr-2 data-[orientation=vertical]:h-4"
              />
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>Provider {activeItem}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
            {showPatientTable ? (
              <PatientTable 
                patients={assignedPatients} 
                providerId={providerId}
                onPatientClick={onPatientClick}
              />
            ) : (
              <>
                <ProviderVisits 
                  providerId={providerId}
                  assignedPatients={assignedPatients}
                  onVisitClick={onVisitClick}
                  onCountChange={handleVisitsCountChange}
                  onLoadingChange={handleVisitsLoadingChange}
                />
              </>
            )}
          </div>
        </SidebarInset>
      </SidebarProvider>
  )
}